import * as React from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import OutlinedInput from '@mui/material/OutlinedInput';
import FormHelperText from '@mui/material/FormHelperText';
import FormControl from '@mui/material/FormControl';
import { requestPasswordReset } from './requestPasswordReset';
import RequestStatusDialog from './RequestStatusDialog';

interface ForgotPasswordProps {
  open: boolean;
  handleClose: () => void;
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function ForgotPassword({ open, handleClose }: ForgotPasswordProps) {
  const [email, setEmail] = React.useState('');
  const [emailError, setEmailError] = React.useState(false);
  const [emailErrorMessage, setEmailErrorMessage] = React.useState('');

  const [statusDialogOpen, setStatusDialogOpen] = React.useState(false);
  const [isSuccess, setIsSuccess] = React.useState(false);

  const handleStatusDialogClose = () => {
    setStatusDialogOpen(false);
    handleClose();
  };

  const validateEmail = (value: string): boolean => {
    if (!value) {
      setEmailError(true);
      setEmailErrorMessage('Email address is required.');
      return false;
    }
    if (!EMAIL_REGEX.test(value)) {
      setEmailError(true);
      setEmailErrorMessage('Please enter a valid email address.');
      return false;
    }
    setEmailError(false);
    setEmailErrorMessage('');
    return true;
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(event.target.value);
    validateEmail(event.target.value);
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const isValid = validateEmail(email);

    if (isValid) {
      handleClose();

      try {
        const responseStatus = await requestPasswordReset(email);

        if (responseStatus === 200) {
          setIsSuccess(true);
        } else {
          setIsSuccess(false);
        }
      } catch (error) {
        setIsSuccess(false);
        console.error("Request failed:", error);
      } finally {
        setStatusDialogOpen(true);
      }
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        PaperProps={{
          component: 'form',
          onSubmit: handleSubmit,
        }}
      >
        <DialogTitle>Reset password</DialogTitle>
        <DialogContent
          sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}
        >
          <DialogContentText>
            Enter your account&apos;s email address, and we&apos;ll send you a link to
            reset your password.
          </DialogContentText>
          <FormControl error={emailError} fullWidth>
            <OutlinedInput
              autoFocus
              required
              margin="dense"
              id="email"
              name="email"
              label="Email address"
              placeholder="Email address"
              type="email"
              fullWidth
              value={email}
              onChange={handleInputChange}
              onBlur={() => validateEmail(email)}
            />
            {emailError && <FormHelperText>{emailErrorMessage}</FormHelperText>}
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ pb: 3, px: 3 }}>
          <Button onClick={handleClose}>Cancel</Button>
          <Button variant="contained" type="submit">
            Continue
          </Button>
        </DialogActions>
      </Dialog>

      {}
      <RequestStatusDialog
        open={statusDialogOpen}
        handleClose={handleStatusDialogClose}
        email={email}
        isSuccess={isSuccess}
      />
    </>
  );
}